import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { CheckCircle, Lock, Clock, DollarSign, Database, Code, BrainCircuit, Flame } from 'lucide-react';
import { LEARNING_DATA } from '../data/learningData';

const tileVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1 }
};

const FlipCardsPage = () => {
  const { flippedCards, setFlippedCards, setBalance, addCp } = useGame();
  const [timeLeft, setTimeLeft] = useState('');
  const [activeCategory, setActiveCategory] = useState('SQL');

  // --- TIMER (Resets at Midnight) ---
  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
      const diff = tomorrow - now;

      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      return `${hours}h ${minutes}m ${seconds}s`;
    };

    const timer = setInterval(() => setTimeLeft(calculateTime()), 1000);
    setTimeLeft(calculateTime());
    return () => clearInterval(timer);
  }, []);

  // --- FLIP LOGIC (UNCHANGED) ---
  const handleCardClick = (id) => {
    if (flippedCards.includes(id)) return;
    if (flippedCards.length >= 4) return;

    setFlippedCards(id, activeCategory);
    setBalance((prev) => prev + 100);
    addCp(50);
  };

  const isLocked = flippedCards.length >= 4;

  // Cards for current category (no slice – wall view)
  const currentCards = LEARNING_DATA[activeCategory] || [];

  const theme = useMemo(() => {
    if (activeCategory === 'SQL') {
      return {
        label: 'SQL',
        accentText: 'text-emerald-300',
        rail: 'from-emerald-400/70 via-cyan-400/40 to-transparent',
        chip: 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200',
        glow: 'shadow-[0_0_25px_rgba(16,185,129,0.45)]',
        icon: <Database size={16} />
      };
    }
    if (activeCategory === 'Python') {
      return {
        label: 'Python',
        accentText: 'text-cyan-200',
        rail: 'from-cyan-400/70 via-purple-500/35 to-transparent',
        chip: 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100',
        glow: 'shadow-[0_0_25px_rgba(34,211,238,0.45)]',
        icon: <Code size={16} />
      };
    }
    return {
      label: 'AI / ML',
      accentText: 'text-purple-200',
      rail: 'from-purple-500/70 via-cyan-400/35 to-transparent',
      chip: 'border-purple-400/40 bg-purple-500/10 text-purple-100',
      glow: 'shadow-[0_0_25px_rgba(168,85,247,0.45)]',
      icon: <BrainCircuit size={16} />
    };
  }, [activeCategory]);

  return (
    <div className="relative h-full w-full bg-black overflow-hidden flex flex-col">

      {/* ===== CYBERPUNK BACKGROUND (MATCHING THEME) ===== */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-[200%] h-[200%]
          bg-[radial-gradient(circle,rgba(139,92,246,0.35),rgba(0,0,0,0.9)_55%)]"
          animate={{ rotate: 360 }}
          transition={{ duration: 220, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0
          bg-[linear-gradient(120deg,transparent,rgba(34,211,238,0.25),transparent)]"
          animate={{ x: ['-120%', '120%'] }}
          transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute inset-0
          bg-[radial-gradient(circle_at_50%_80%,rgba(34,211,238,0.14),transparent_60%)]"
          animate={{ scale: [1, 1.25, 1] }}
          transition={{ duration: 28, repeat: Infinity, ease: "easeInOut" }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0)_35%,rgba(0,0,0,0.96)_100%)]" />
      </div>

      {/* ===== STICKY HUD ===== */}
      <div className="sticky top-0 z-20 bg-black/70 backdrop-blur-xl border-b border-cyan-400/20 px-6 py-4">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-4">

          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl border border-cyan-400/25 bg-black/50 text-cyan-200">
              <Flame size={18} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-cyan-200/70 font-bold">
                High Roller Cards
              </div>
              <div className="text-2xl md:text-3xl font-black tracking-widest uppercase text-cyan-300 drop-shadow-[0_0_10px_rgba(34,211,238,0.8)]">
                Flip & Collect
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="bg-black/50 border border-cyan-400/30 px-4 py-2 rounded-full text-sm font-bold text-white">
              Daily Limit:{" "}
              <span className={flippedCards.length >= 4 ? "text-red-400" : "text-emerald-400"}>
                {flippedCards.length} / 4
              </span>
            </div>

            <div className="bg-black/50 border border-purple-400/30 px-4 py-2 rounded-full text-sm font-bold text-purple-200 flex items-center gap-2">
              <DollarSign size={14} className="text-yellow-300" />
              $100 per flip
            </div>

            {isLocked && (
              <div className="bg-red-900/25 border border-red-500/40 px-4 py-2 rounded-full text-sm font-bold text-red-300 flex items-center gap-2 animate-pulse">
                <Clock size={16} /> Resets in: <span className="font-mono text-white">{timeLeft}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="relative z-10 flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 py-6 pb-24">

          {/* ===== CATEGORY CONTROL PANEL ===== */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap gap-2 bg-black/40 border border-cyan-400/15 rounded-2xl p-2 backdrop-blur-md">
              <button
                onClick={() => setActiveCategory('SQL')}
                className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all
                ${activeCategory === 'SQL'
                    ? 'bg-emerald-500/20 text-emerald-200 border border-emerald-400/40 shadow-[0_0_18px_rgba(16,185,129,0.35)]'
                    : 'text-slate-400 hover:text-white'}
                `}
              >
                <Database size={14} /> SQL
              </button>

              <button
                onClick={() => setActiveCategory('Python')}
                className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all
                ${activeCategory === 'Python'
                    ? 'bg-cyan-500/15 text-cyan-100 border border-cyan-400/40 shadow-[0_0_18px_rgba(34,211,238,0.35)]'
                    : 'text-slate-400 hover:text-white'}
                `}
              >
                <Code size={14} /> Python
              </button>

              <button
                onClick={() => setActiveCategory('AI/ML')}
                className={`px-5 py-2 rounded-xl text-xs font-black uppercase tracking-wider flex items-center gap-2 transition-all
                ${activeCategory === 'AI/ML'
                    ? 'bg-purple-500/15 text-purple-100 border border-purple-400/40 shadow-[0_0_18px_rgba(168,85,247,0.35)]'
                    : 'text-slate-400 hover:text-white'}
                `}
              >
                <BrainCircuit size={14} /> AI / ML
              </button>
            </div>

            <div className="flex items-center gap-2 text-xs font-mono text-cyan-200/60">
              <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border ${theme.chip}`}>
                {theme.icon} Viewing: <span className="font-bold">{theme.label}</span>
              </span>
              <span className="hidden md:inline text-cyan-200/35">•</span>
              <span className="hidden md:inline">Flip any 4 per day across categories</span>
            </div>
          </div>

          {/* ===== CARD WALL ===== */}
          <motion.div
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-5"
          >
            {currentCards.map((card) => {
              const isFlipped = flippedCards.includes(card.id);
              const isCardLocked = !isFlipped && isLocked;

              return (
                <motion.div
                  key={card.id}
                  variants={tileVariants}
                  whileHover={!isCardLocked ? { scale: 1.03 } : {}}
                  className={`group h-72 w-full ${isCardLocked ? 'opacity-60' : ''}`}
                  style={{ perspective: '1200px' }}
                  onClick={() => !isCardLocked && handleCardClick(card.id)}
                >
                  <div
                    className="relative h-full w-full transition-transform duration-700"
                    style={{
                      transformStyle: 'preserve-3d',
                      transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
                    }}
                  >
                    {/* ===== FRONT ===== */}
                    <div
                      className={`absolute inset-0 rounded-2xl border bg-slate-950/50 backdrop-blur-md p-5 shadow-2xl
                      ${isCardLocked ? 'border-red-500/25' : 'border-cyan-400/15 hover:border-cyan-300/40'}
                      `}
                      style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                    >
                      {/* Left neon rail */}
                      <div className={`absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b ${theme.rail}`} />

                      {/* Scanline */}
                      {!isCardLocked && (
                        <motion.div
                          className="absolute inset-0 rounded-2xl bg-[linear-gradient(180deg,transparent,rgba(34,211,238,0.06),transparent)]"
                          animate={{ y: ['-120%', '120%'] }}
                          transition={{ duration: 7.5, repeat: Infinity, ease: 'linear' }}
                        />
                      )}

                      <div className="relative flex flex-col h-full">
                        <div className="flex items-start justify-between">
                          <div className="text-[10px] font-black uppercase tracking-widest text-cyan-200/60">
                            High Roller Card
                          </div>

                          {isCardLocked ? (
                            <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-red-300">
                              <Lock size={12} /> Locked
                            </div>
                          ) : (
                            <div className={`text-[10px] font-black uppercase tracking-widest ${theme.accentText}`}>
                              {theme.label}
                            </div>
                          )}
                        </div>

                        <div className="mt-6 flex items-center justify-center">
                          <div className="p-4 rounded-2xl bg-black/55 border border-cyan-400/15 text-cyan-200">
                            {activeCategory === 'SQL' ? <Database size={34} /> : activeCategory === 'Python' ? <Code size={34} /> : <BrainCircuit size={34} />}
                          </div>
                        </div>

                        <h3 className="mt-5 text-lg font-black text-white tracking-widest uppercase text-center line-clamp-2">
                          {card.term}
                        </h3>

                        <div className="mt-auto flex items-center justify-center">
                          <div className="inline-flex items-center gap-1 text-xs font-mono text-yellow-300 bg-yellow-300/10 px-3 py-1 rounded border border-yellow-300/20">
                            <DollarSign size={12} /> REWARD: $100
                          </div>
                        </div>
                      </div>

                      {/* Shield overlay when locked */}
                      {isCardLocked && (
                        <div className="absolute inset-0 rounded-2xl bg-black/65 backdrop-blur-[1px] flex items-center justify-center">
                          <div className="px-4 py-2 rounded-full border border-red-500/30 bg-red-500/10 text-red-300 font-black tracking-widest text-xs uppercase">
                            Daily limit reached
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ===== BACK ===== */}
                    <div
                      className={`absolute inset-0 rounded-2xl border p-5 shadow-2xl text-center
                      bg-gradient-to-br from-slate-900/70 to-black backdrop-blur-md ${theme.glow}
                      `}
                      style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)'
                      }}
                    >
                      <div className={`absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b ${theme.rail}`} />

                      <div className="flex flex-col h-full">
                        <div className={`text-[10px] font-black uppercase tracking-widest ${theme.accentText}`}>
                          Data Unlocked
                        </div>

                        <h3 className={`mt-3 text-base font-black uppercase tracking-wider border-b pb-2 ${theme.accentText} border-cyan-400/15`}>
                          {card.term}
                        </h3>

                        <div className="mt-3 text-left text-sm text-slate-100/90 overflow-y-auto max-h-[140px] pr-1">
                          {card.definition}
                        </div>

                        <div className={`mt-auto pt-4 flex items-center justify-center gap-2 text-xs font-black uppercase tracking-widest ${theme.accentText}`}>
                          <CheckCircle size={16} /> Collected
                        </div>
                      </div>
                    </div>

                  </div>
                </motion.div>
              );
            })}
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default FlipCardsPage;
